<script setup lang="ts">
import type { ClassValue } from 'tailwind-variants';
import type { Component } from 'vue';
import { computed } from 'vue';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'reka-ui';
import { Link as InertiaLink } from '@inertiajs/vue3';
import { dropdownMenuVariants } from './index.ts';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';
type ItemVariant = 'default' | 'destructive';

export type DropdownActionItem = {
    type?: 'item';
    label: string;
    icon?: Component;
    href?: string;
    external?: boolean;
    method?: string;
    variant?: ItemVariant;
    disabled?: boolean;
    onSelect?: () => void;
};

export type DropdownSeparatorItem = {
    type: 'separator';
};

export type DropdownLabelItem = {
    type: 'label';
    label: string;
    sublabel?: string;
};

export type DropdownItem = DropdownActionItem | DropdownSeparatorItem | DropdownLabelItem;

type DropdownItemInput = {
    type?: string;
    label?: string;
    sublabel?: string;
    icon?: Component;
    href?: string;
    external?: boolean;
    method?: string;
    variant?: ItemVariant;
    disabled?: boolean;
    onSelect?: () => void;
};

export type DropdownItemEntry = DropdownItemInput | false | null | undefined;

const props = withDefaults(defineProps<{
    /**
     * Menu entries. Each item is either an action (`type: 'item'` — the default),
     * a visual `'separator'`, or a non-interactive `'label'` block.
     * Action items support `label`, `icon`, `href` (Inertia `<Link>` by default; `<a>` when `external`),
     * `method` (forwarded to `<Link>`), `variant: 'default' | 'destructive'`,
     * `disabled`, and `onSelect`. Falsy entries are filtered out so consumers can
     * splice conditional items with `cond && {…}`.
     */
    items?: DropdownItemEntry[];
    /**
     * Which side of the trigger the menu opens on.
     * @values 'top', 'right', 'bottom', 'left'
     * @default 'bottom'
     */
    side?: Side;
    /**
     * Alignment of the menu against the trigger along the chosen `side`.
     * @values 'start', 'center', 'end'
     * @default 'start'
     */
    align?: Align;
    /**
     * Distance in pixels between the trigger and the menu.
     * @default 4
     */
    sideOffset?: number;
    /**
     * Extra classes merged onto the menu content panel. Use to override
     * width / padding at call sites.
     */
    contentClass?: ClassValue;
}>(), {
    items: () => [],
    side: 'bottom',
    align: 'start',
    sideOffset: 4,
    contentClass: undefined,
});

const isOpen = defineModel<boolean | undefined>('open', { default: undefined });

const ui = computed(() => dropdownMenuVariants());

const visibleItems = computed<DropdownItemInput[]>(
    () => props.items.filter((item): item is DropdownItemInput => Boolean(item)),
);

const resolveLinkComponent = (item: DropdownItemInput) => {
    if (item.disabled) {
        return 'span';
    }

    if (item.external) {
        return 'a';
    }

    return InertiaLink;
};
</script>

<template>
    <DropdownMenuRoot v-model:open="isOpen">
        <DropdownMenuTrigger as-child>
            <slot name="trigger" />
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
            <DropdownMenuContent
                :side="side"
                :align="align"
                :side-offset="sideOffset"
                :class="ui.content({ class: contentClass })"
            >
                <template
                    v-for="(item, index) in visibleItems"
                    :key="index"
                >
                    <DropdownMenuSeparator
                        v-if="item.type === 'separator'"
                        :class="ui.separator()"
                    />

                    <DropdownMenuLabel
                        v-else-if="item.type === 'label'"
                        :class="ui.label()"
                    >
                        <div class="font-medium text-gray-900">
                            {{ item.label }}
                        </div>
                        <div
                            v-if="item.sublabel"
                            class="text-xs text-gray-600"
                        >
                            {{ item.sublabel }}
                        </div>
                    </DropdownMenuLabel>

                    <DropdownMenuItem
                        v-else
                        as-child
                        :disabled="item.disabled"
                        @select="(event) => {
                            if (item.disabled) {
                                event.preventDefault();
                                return;
                            }
                            item.onSelect?.();
                        }"
                    >
                        <component
                            :is="item.href ? resolveLinkComponent(item) : 'button'"
                            :href="item.href && !item.disabled ? item.href : undefined"
                            :method="item.href && item.method ? item.method : undefined"
                            :type="item.href ? undefined : 'button'"
                            :disabled="!item.href ? item.disabled : undefined"
                            :data-variant="item.variant ?? 'default'"
                            :class="ui.item({ variant: item.variant ?? 'default', disabled: item.disabled })"
                        >
                            <slot
                                name="item"
                                :item="item"
                            >
                                <component
                                    :is="item.icon"
                                    v-if="item.icon"
                                    class="pointer-events-none size-4 shrink-0 opacity-60"
                                    aria-hidden="true"
                                />
                                <span>{{ item.label }}</span>
                            </slot>
                        </component>
                    </DropdownMenuItem>
                </template>
            </DropdownMenuContent>
        </DropdownMenuPortal>
    </DropdownMenuRoot>
</template>
